/**
 * Página /#agente-noc — Monitoramento → Agente NOC IA (2026-09-28).
 *
 * O agente (claude-hq) diagnostica os alarmes do Zabbix só com leitura e grava em tabelas noc_* deste banco.
 * Aqui a equipe acompanha, conversa com o agente (a pergunta fica pendente e ele responde em ~30s) e avalia.
 *
 *   #agente-noc               lista
 *   #agente-noc/<id>          incidente
 *   #agente-noc/maturidade    KPIs e gates
 *   #agente-noc/chats         conversas livres ("Novo chat", sem incidente) + consumo por usuário (2026-09-29)
 *   #agente-noc/alarmes       alarmes ativos do Zabbix em tempo real; ADMIN tem o botão "Analisar com IA" (#858, 2026-10-03)
 *   #agente-noc/chats/novo    nova conversa: a 1ª pergunta cria o incidente tipo 'chat' e abre #agente-noc/<id>
 */
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';

type Linha = {
  id: number; categoria: string; site: string; titulo: string; status: string; flap: boolean; reaberturas: number;
  abertoEm: string; resolvidoEm: string | null; diagEm: string | null; custoUsd: number; resumo: string | null;
  confianca: number | null; falhaMonitoramento: boolean | null; abririaOs: boolean | null; nEventos: number;
  avaliacao: string | null;
};
type Diag = {
  resumo: string; causa_provavel: string; confianca: number; falha_de_monitoramento: boolean; impacto: string;
  clientes_afetados_estimado: number | null;
  evidencias: Array<{ fonte: string; comando_ou_consulta: string; trecho: string }>;
  os_rascunho: { assunto: string; texto: string }; proximos_passos: string[];
};
type Detalhe = {
  incidente: {
    id: number; categoria: string; site: string; titulo: string; status: string; flap: boolean; reaberturas: number;
    abertoEm: string; resolvidoEm: string | null; diagEm: string | null; diagErro: string | null;
    diagnostico: Diag | null; custoUsd: number; ackEventids: string[]; tipo: 'alarme' | 'chat'; criadoPor: string | null;
    criadoPorEmail: string | null;
  };
  eventos: Array<{ eventid: string; nome: string; severidade: number; host: string; inicio: string; fim: string | null; preExistente: boolean }>;
  decisao: { abriria: boolean; motivo: string } | null;
  os: Array<{ assunto: string; texto: string; regra: string; aberta: boolean; criadoEm: string }>;
  mensagens: Array<{
    id: number; papel: string; usuario: string | null; texto: string; pendente: boolean; criadoEm: string;
    avaliacoes: Array<{ usuario: string; util: boolean; comentario: string | null; criadoEm: string; minha: boolean }>;
  }>;
  avaliacoes: Array<{ usuario: string; diagnostico: string | null; osProcedente: boolean | null; ruido: boolean | null; comentario: string | null; criadoEm: string }>;
  tools: Array<{ tool: string; permitido: boolean; motivo: string | null; args: Record<string, unknown>; criadoEm: string }>;
  consumo: { execucoes: number; tokensEntrada: number; tokensSaida: number } | null;
};
type Mensagem = Detalhe['mensagens'][number];
type Tool = Detalhe['tools'][number];
type ChatLinha = {
  id: number; titulo: string; agente: string | null; criadoPor: string | null; criadoPorEmail: string | null; abertoEm: string; ultimaEm: string;
  custoUsd: number; perguntas: number; ultimoPapel: string | null; uteis: number; ruins: number;
};
type ConsumoUsuario = {
  usuario: string | null; email: string | null; chats: number; respostas: number; custoUsd: number;
  tokensEntrada: number; tokensSaida: number; uteis: number; ruins: number;
};

type Tarefa = { inboxId: number; agente: string; titulo: string; status: string; prioridade: string; descricao: string; criadoEm: string; atualizadoEm: string };
const AGENTES: Array<{ id: 'noc-zabbix' | 'noc-redefora'; nome: string; desc: string }> = [
  { id: 'noc-zabbix', nome: 'noc-zabbix', desc: 'Servidor, infraestrutura e aplicação: Zabbix, equipamentos (Huawei, Datacom, MikroTik… só leitura), RAG e stacks.' },
  { id: 'noc-redefora', nome: 'noc-redefora', desc: 'Queda massiva de PPPoE: interfaces com ONUs offline, OLT/PON, motivo da queda (energia × fibra) e CTO, lidos do IXC.' }
];
const PRIO_COR: Record<string, string> = { critica: 'text-rose-400', alta: 'text-amber-400', media: 'text-slate-300', baixa: 'text-slate-500' };

const STATUS_COR: Record<string, string> = {
  aberto: 'text-amber-400', fila: 'text-amber-300', diagnosticando: 'text-cyan-400', diagnosticado: 'text-emerald-400',
  erro: 'text-rose-400', resolvido: 'text-slate-400', auto_resolvido: 'text-slate-500'
};
const AVAL_COR: Record<string, string> = { certo: 'text-emerald-400', parcial: 'text-amber-400', errado: 'text-rose-400' };

const dt = (s: string | null) => (s ? new Date(s).toLocaleString('pt-BR', { hour12: false }) : '—');
const usd = (n: number | null | undefined) => (n == null ? '—' : `US$ ${n.toFixed(2)}`);
const go = (h: string) => { window.location.hash = '#agente-noc' + (h ? '/' + h : ''); };
const tok = (n: number | null | undefined) => (n == null ? '—' : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${Math.round(n / 1e3)}k` : String(n));

export function NocAgentePage({ subRoute, isAdmin = false }: { subRoute: string | null; isAdmin?: boolean }) {
  if (subRoute === 'alarmes') return <Alarmes isAdmin={isAdmin} />;
  if (subRoute === 'maturidade') return <Maturidade />;
  if (subRoute === 'chats') return <Chats />;
  if (subRoute === 'chats/novo') return <NovoChat />;
  if (subRoute === 'tarefas') return <Tarefas />;
  if (subRoute && /^\d+$/.test(subRoute)) return <Incidente id={Number(subRoute)} />;
  return <Lista />;
}

function Abas({ atual }: { atual: 'lista' | 'alarmes' | 'chats' | 'tarefas' | 'maturidade' }) {
  const b = (on: boolean) => `px-3 py-1 rounded text-sm ${on ? 'bg-slate-700 text-white' : 'text-slate-400 hover:bg-slate-800'}`;
  return (
    <div className="flex items-center gap-2">
      <button className={b(atual === 'lista')} onClick={() => go('')}>Incidentes</button>
      <button className={b(atual === 'alarmes')} onClick={() => go('alarmes')}>Alarmes</button>
      <button className={b(atual === 'chats')} onClick={() => go('chats')}>Chats</button>
      <button className={b(atual === 'tarefas')} onClick={() => go('tarefas')}>Tarefas</button>
      <button className={b(atual === 'maturidade')} onClick={() => go('maturidade')}>Maturidade</button>
    </div>
  );
}

type Filtros = {
  status: 'ativos' | 'resolvidos' | 'todos' | 'sem_avaliacao';
  avaliacao: 'todas' | 'sem' | 'certo' | 'parcial' | 'errado';
  os: 'todas' | 'abriria' | 'nao';
  monitoramento: 'todos' | 'sim' | 'nao';
  categoria: string;
  site: string;
  dias: number;
};
const FILTROS_PADRAO: Filtros = { status: 'ativos', avaliacao: 'todas', os: 'todas', monitoramento: 'todos', categoria: '', site: '', dias: 0 };
const CHAVE_FILTROS = 'nms.noc-agente.filtros';

function lerFiltros(): Filtros {
  try {
    const salvo = JSON.parse(localStorage.getItem(CHAVE_FILTROS) ?? 'null');
    return salvo ? { ...FILTROS_PADRAO, ...salvo } : FILTROS_PADRAO;
  } catch {
    return FILTROS_PADRAO;
  }
}

function Lista() {
  const [filtros, setFiltros] = useState<Filtros>(lerFiltros);
  const [texto, setTexto] = useState('');
  const [busca, setBusca] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setBusca(texto.trim()), 400);
    return () => clearTimeout(t);
  }, [texto]);
  useEffect(() => {
    try { localStorage.setItem(CHAVE_FILTROS, JSON.stringify(filtros)); } catch { /* sem storage */ }
  }, [filtros]);
  const set = <K extends keyof Filtros>(k: K, v: Filtros[K]) => setFiltros((f) => ({ ...f, [k]: v }));

  const params = new URLSearchParams({
    status: filtros.status, avaliacao: filtros.avaliacao, os: filtros.os, monitoramento: filtros.monitoramento,
    dias: String(filtros.dias), take: '500'
  });
  if (filtros.categoria) params.set('categoria', filtros.categoria);
  if (filtros.site) params.set('site', filtros.site);
  if (busca) params.set('q', busca);

  const q = useQuery<{ incidentes: Linha[]; categorias: string[]; sites: string[] }>({
    queryKey: ['noc-agente', 'lista', params.toString()],
    queryFn: () => api.get(`/api/noc-agente/incidentes?${params}`),
    refetchInterval: 20_000,
    placeholderData: (prev) => prev
  });
  const f = (on: boolean) => `px-2 py-1 rounded text-xs ring-1 ${on ? 'ring-emerald-500 text-emerald-300' : 'ring-slate-700 text-slate-400'}`;
  const sel = 'bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200';
  const alterado = JSON.stringify(filtros) !== JSON.stringify(FILTROS_PADRAO) || texto !== '';
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xl font-semibold">Agente NOC IA <span className="text-sm text-slate-500">— diagnóstico só leitura, modo sombra</span></h2>
        <Abas atual="lista" />
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button className={f(filtros.status === 'ativos')} onClick={() => set('status', 'ativos')}>Ativos</button>
        <button className={f(filtros.status === 'resolvidos')} onClick={() => set('status', 'resolvidos')}>Resolvidos</button>
        <button className={f(filtros.status === 'sem_avaliacao')} onClick={() => set('status', 'sem_avaliacao')}>Sem avaliação</button>
        <button className={f(filtros.status === 'todos')} onClick={() => set('status', 'todos')}>Todos</button>
        <span className="mx-1 h-4 w-px bg-slate-700" />
        <select className={sel} value={filtros.categoria} onChange={(e) => set('categoria', e.target.value)}>
          <option value="">Categoria: todas</option>
          {q.data?.categorias.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className={sel} value={filtros.site} onChange={(e) => set('site', e.target.value)}>
          <option value="">Site: todos</option>
          {q.data?.sites.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className={sel} value={filtros.avaliacao} onChange={(e) => set('avaliacao', e.target.value as Filtros['avaliacao'])}>
          <option value="todas">Avaliação: todas</option>
          <option value="sem">sem avaliação</option>
          <option value="certo">certo</option>
          <option value="parcial">parcial</option>
          <option value="errado">errado</option>
        </select>
        <select className={sel} value={filtros.os} onChange={(e) => set('os', e.target.value as Filtros['os'])}>
          <option value="todas">OS: todas</option>
          <option value="abriria">abriria OS</option>
          <option value="nao">não abriria</option>
        </select>
        <select className={sel} value={filtros.monitoramento} onChange={(e) => set('monitoramento', e.target.value as Filtros['monitoramento'])}>
          <option value="todos">Monitoramento: todos</option>
          <option value="sim">só falha de monitoramento</option>
          <option value="nao">sem falha de monitoramento</option>
        </select>
        <select className={sel} value={filtros.dias} onChange={(e) => set('dias', Number(e.target.value))}>
          <option value={0}>Período: tudo</option>
          <option value={1}>últimas 24h</option>
          <option value={7}>últimos 7 dias</option>
          <option value={30}>últimos 30 dias</option>
        </select>
        <input className={`${sel} w-56`} value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="buscar #id, título, site, resumo" />
        {alterado && (
          <button className="text-xs text-slate-400 hover:text-slate-200" onClick={() => { setFiltros(FILTROS_PADRAO); setTexto(''); }}>limpar</button>
        )}
        <span className="ml-auto text-xs text-slate-500">{q.data ? `${q.data.incidentes.length} incidente(s)` : ''}{q.isFetching ? ' · atualizando…' : ''}</span>
      </div>
      <div className="rounded border border-slate-800 bg-slate-900/60 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-800/60 text-slate-400">
            <tr>
              <th className="text-left px-3 py-2">#</th>
              <th className="text-left px-3 py-2">Aberto</th>
              <th className="text-left px-3 py-2">Categoria / site</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Diagnóstico</th>
              <th className="text-left px-3 py-2">OS</th>
              <th className="text-left px-3 py-2">Avaliação</th>
              <th className="text-right px-3 py-2">Custo</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && <tr><td colSpan={8} className="p-4 text-center text-slate-500">carregando…</td></tr>}
            {q.isError && <tr><td colSpan={8} className="p-4 text-center text-rose-400">erro ao carregar</td></tr>}
            {q.data?.incidentes.map((i) => (
              <tr key={i.id} className="border-t border-slate-800 hover:bg-slate-800/40 cursor-pointer" onClick={() => go(String(i.id))}>
                <td className="px-3 py-1.5 font-mono text-xs text-slate-400">{i.id}</td>
                <td className="px-3 py-1.5 font-mono text-xs text-slate-400 whitespace-nowrap">{dt(i.abertoEm)}</td>
                <td className="px-3 py-1.5">
                  <div className="text-slate-200">{i.categoria} · {i.site}</div>
                  <div className="text-xs text-slate-500 truncate max-w-md" title={i.titulo}>{i.titulo}</div>
                </td>
                <td className={`px-3 py-1.5 text-xs ${STATUS_COR[i.status] ?? ''}`}>
                  {i.status}{i.flap ? ' · flap' : ''}
                </td>
                <td className="px-3 py-1.5 text-xs text-slate-300 max-w-lg">
                  {i.falhaMonitoramento && <span className="px-1.5 py-0.5 mr-1 rounded text-[10px] ring-1 ring-violet-500 text-violet-300">monitoramento</span>}
                  <span className="line-clamp-2" title={i.resumo ?? ''}>{i.resumo ?? '—'}</span>
                </td>
                <td className="px-3 py-1.5 text-xs">{i.abririaOs == null ? '—' : i.abririaOs ? <span className="text-amber-400">abriria</span> : <span className="text-slate-500">não</span>}</td>
                <td className={`px-3 py-1.5 text-xs ${AVAL_COR[i.avaliacao ?? ''] ?? 'text-slate-500'}`}>{i.avaliacao ?? '—'}</td>
                <td className="px-3 py-1.5 text-xs text-right text-slate-400 whitespace-nowrap">{usd(i.custoUsd)}</td>
              </tr>
            ))}
            {q.data?.incidentes.length === 0 && <tr><td colSpan={8} className="p-4 text-center text-slate-500">nenhum incidente</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Card({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className="rounded border border-slate-800 bg-slate-900/60 p-4 space-y-2">
      <div className="text-sm font-semibold text-slate-300">{titulo}</div>
      {children}
    </div>
  );
}

function Incidente({ id }: { id: number }) {
  const qc = useQueryClient();
  const q = useQuery<Detalhe>({
    queryKey: ['noc-agente', 'incidente', id],
    queryFn: () => api.get(`/api/noc-agente/incidentes/${id}`),
    refetchInterval: (query) => {
      const d = query.state.data as Detalhe | undefined;
      const esperando = d?.mensagens.some((m) => m.papel === 'usuario')
        && d.mensagens[d.mensagens.length - 1]?.papel === 'usuario';
      return esperando || d?.incidente.status === 'diagnosticando' ? 5_000 : 30_000;
    }
  });
  const [texto, setTexto] = useState('');
  const enviar = useMutation({
    mutationFn: () => api.post(`/api/noc-agente/incidentes/${id}/mensagens`, { texto }),
    onSuccess: () => { setTexto(''); qc.invalidateQueries({ queryKey: ['noc-agente', 'incidente', id] }); }
  });

  if (q.isLoading) return <div className="p-6 text-slate-400">carregando…</div>;
  if (q.isError || !q.data) return <div className="p-6 text-rose-400">incidente não encontrado</div>;
  const { incidente: inc, eventos, decisao, os, mensagens, avaliacoes, tools } = q.data;
  const d = inc.diagnostico;
  const aguardando = mensagens.length > 0 && mensagens[mensagens.length - 1].papel === 'usuario';

  if (inc.tipo === 'chat') {
    const c = q.data.consumo;
    return (
      <div className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <button className="text-xs text-slate-400 hover:text-slate-200" onClick={() => go('chats')}>← chats</button>
            <h2 className="text-xl font-semibold">Chat #{inc.id}</h2>
            <div className="text-sm text-slate-400">{inc.titulo}</div>
          </div>
          <div className="text-right text-xs text-slate-400 space-y-0.5">
            <div>aberto por {inc.criadoPor ?? '—'}{inc.criadoPorEmail ? ` (${inc.criadoPorEmail})` : ''} · {dt(inc.abertoEm)}</div>
            <div>custo {usd(inc.custoUsd)} · {c?.execucoes ?? 0} resposta(s)</div>
            <div>tokens {tok(c?.tokensEntrada)} entrada · {tok(c?.tokensSaida)} saída</div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <Card titulo="Conversa com o agente">
              <Conversa incidenteId={inc.id} mensagens={mensagens} aguardando={aguardando} texto={texto} setTexto={setTexto} enviar={enviar} alto />
            </Card>
          </div>
          <div className="space-y-4">
            <Consultas tools={tools} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <button className="text-xs text-slate-400 hover:text-slate-200" onClick={() => go('')}>← incidentes</button>
          <h2 className="text-xl font-semibold">#{inc.id} · {inc.categoria} · {inc.site}</h2>
          <div className="text-sm text-slate-400">{inc.titulo}</div>
        </div>
        <div className="text-right text-xs text-slate-400 space-y-0.5">
          <div className={STATUS_COR[inc.status] ?? ''}>{inc.status}{inc.flap ? ` · flap (${inc.reaberturas} reaberturas)` : ''}</div>
          <div>aberto {dt(inc.abertoEm)} · diagnóstico {dt(inc.diagEm)}</div>
          {inc.resolvidoEm && <div>resolvido {dt(inc.resolvidoEm)}</div>}
          <div>custo {usd(inc.custoUsd)}</div>
        </div>
      </div>

      {inc.diagErro && <div className="rounded border border-rose-800 bg-rose-950/40 p-3 text-sm text-rose-300">Erro no diagnóstico: {inc.diagErro}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card titulo="Diagnóstico">
            {!d && <div className="text-sm text-slate-500">ainda sem diagnóstico</div>}
            {d && (
              <div className="space-y-3 text-sm">
                <div className="flex gap-2 flex-wrap text-xs">
                  <span className="px-1.5 py-0.5 rounded ring-1 ring-slate-600">confiança {Math.round(d.confianca * 100)}%</span>
                  {d.falha_de_monitoramento && <span className="px-1.5 py-0.5 rounded ring-1 ring-violet-500 text-violet-300">falha de monitoramento</span>}
                  {d.clientes_afetados_estimado != null && <span className="px-1.5 py-0.5 rounded ring-1 ring-amber-600 text-amber-300">~{d.clientes_afetados_estimado} clientes</span>}
                </div>
                <p className="text-slate-200">{d.resumo}</p>
                <div><span className="text-slate-400">Causa provável: </span>{d.causa_provavel}</div>
                <div><span className="text-slate-400">Impacto: </span>{d.impacto}</div>
                {d.proximos_passos?.length > 0 && (
                  <div>
                    <div className="text-slate-400">Próximos passos</div>
                    <ul className="list-disc ml-5 text-slate-300">{d.proximos_passos.map((p, i) => <li key={i}>{p}</li>)}</ul>
                  </div>
                )}
                {d.evidencias?.length > 0 && (
                  <details>
                    <summary className="cursor-pointer text-slate-400">Evidências ({d.evidencias.length})</summary>
                    <div className="space-y-2 mt-2">
                      {d.evidencias.map((e, i) => (
                        <div key={i} className="rounded bg-slate-950/60 p-2">
                          <div className="text-xs text-slate-400">{e.fonte} · <span className="font-mono">{e.comando_ou_consulta}</span></div>
                          <pre className="text-xs text-slate-300 whitespace-pre-wrap break-words">{e.trecho}</pre>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
          </Card>

          <Card titulo="Conversa com o agente">
            <Conversa incidenteId={inc.id} mensagens={mensagens} aguardando={aguardando} texto={texto} setTexto={setTexto} enviar={enviar} />
          </Card>

          <Card titulo={`Eventos do Zabbix (${eventos.length})`}>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <tbody>
                  {eventos.map((e) => (
                    <tr key={e.eventid} className="border-t border-slate-800">
                      <td className="px-2 py-1 font-mono text-slate-400 whitespace-nowrap">{dt(e.inicio)}</td>
                      <td className="px-2 py-1 text-slate-400">sev {e.severidade}</td>
                      <td className="px-2 py-1 text-slate-300">{e.host}</td>
                      <td className="px-2 py-1 text-slate-300">{e.nome}</td>
                      <td className={`px-2 py-1 whitespace-nowrap ${e.fim ? 'text-slate-500' : 'text-amber-400'}`}>{e.fim ? `resolvido ${dt(e.fim)}` : 'ativo'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card titulo="OS (modo sombra)">
            {decisao ? (
              <div className="text-sm">
                <div className={decisao.abriria ? 'text-amber-400' : 'text-slate-400'}>{decisao.abriria ? 'abriria OS' : 'não abriria OS'}</div>
                <div className="text-xs text-slate-500">{decisao.motivo}</div>
              </div>
            ) : <div className="text-sm text-slate-500">sem decisão</div>}
            {d?.os_rascunho && (
              <details className="text-xs">
                <summary className="cursor-pointer text-slate-400">rascunho: {d.os_rascunho.assunto}</summary>
                <pre className="whitespace-pre-wrap text-slate-300 mt-1">{d.os_rascunho.texto}</pre>
              </details>
            )}
            {os.length > 0 && <div className="text-xs text-slate-500">{os.length} OS sombra registrada(s)</div>}
          </Card>

          <Avaliar id={id} />

          {avaliacoes.length > 0 && (
            <Card titulo="Avaliações">
              {avaliacoes.map((a, i) => (
                <div key={i} className="text-xs border-t border-slate-800 pt-1">
                  <span className={AVAL_COR[a.diagnostico ?? ''] ?? 'text-slate-400'}>{a.diagnostico ?? '—'}</span>
                  {a.osProcedente != null && <span className="text-slate-400"> · OS {a.osProcedente ? 'procedente' : 'improcedente'}</span>}
                  {a.ruido && <span className="text-violet-300"> · ruído</span>}
                  <span className="text-slate-500"> · {a.usuario} · {dt(a.criadoEm)}</span>
                  {a.comentario && <div className="text-slate-300 whitespace-pre-wrap">{a.comentario}</div>}
                </div>
              ))}
            </Card>
          )}

          <Consultas tools={tools} />
        </div>
      </div>
    </div>
  );
}

function Conversa({ incidenteId, mensagens, aguardando, texto, setTexto, enviar, alto }: {
  incidenteId: number; mensagens: Mensagem[]; aguardando: boolean; texto: string; setTexto: (t: string) => void;
  enviar: { mutate: () => void; isPending: boolean; isError: boolean }; alto?: boolean;
}) {
  return (
    <>
      <div className={`space-y-2 overflow-y-auto ${alto ? 'max-h-[40rem]' : 'max-h-[28rem]'}`}>
        {mensagens.filter((m) => m.papel !== 'sistema' || !m.texto.startsWith('Incidente de replay')).map((m) => (
          <div key={m.id} className={`rounded p-2 text-sm ${m.papel === 'agente' ? 'bg-slate-800/70' : m.papel === 'usuario' ? 'bg-emerald-950/40 ml-8' : 'text-xs text-slate-500'}`}>
            <div className="text-[11px] text-slate-500">{m.papel === 'agente' ? 'agente' : m.usuario ?? m.papel} · {dt(m.criadoEm)}</div>
            {m.papel === 'agente'
              ? <Markdown texto={m.texto} />
              : <div className="whitespace-pre-wrap break-words text-slate-200">{m.texto}</div>}
            {m.papel === 'agente' && <AvaliarResposta incidenteId={incidenteId} m={m} />}
          </div>
        ))}
        {aguardando && <div className="text-xs text-cyan-400">aguardando o agente… (responde em até ~1 min)</div>}
      </div>
      <div className="flex gap-2 items-end">
        <textarea className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm" rows={2}
          value={texto} onChange={(e) => setTexto(e.target.value)}
          placeholder="pergunte ou peça uma verificação (o agente só lê, nunca altera nada)" />
        <button className="px-3 py-1.5 rounded bg-emerald-700 hover:bg-emerald-600 text-sm disabled:opacity-40"
          disabled={!texto.trim() || enviar.isPending} onClick={() => enviar.mutate()}>Enviar</button>
      </div>
      {enviar.isError && <div className="text-xs text-rose-400">falha ao enviar</div>}
    </>
  );
}

/** Markdown das respostas do agente, só o que ele usa: títulos, listas, **negrito**, `código` e bloco ```. Monta
 * elementos React (nada de innerHTML), então texto do agente nunca vira HTML. */
function Markdown({ texto }: { texto: string }) {
  const inline = (t: string, k: string) =>
    t.split(/(`[^`\n]+`|\*\*[^*\n]+\*\*)/g).map((p, i) =>
      p.startsWith('`') && p.endsWith('`') && p.length > 2
        ? <code key={`${k}-${i}`} className="px-1 rounded bg-slate-950/70 text-emerald-300 text-[0.85em] break-all">{p.slice(1, -1)}</code>
        : p.startsWith('**') && p.endsWith('**') && p.length > 4
          ? <strong key={`${k}-${i}`} className="text-white">{p.slice(2, -2)}</strong>
          : p);
  const blocos: React.ReactNode[] = [];
  const linhas = texto.split('\n');
  let i = 0;
  while (i < linhas.length) {
    const l = linhas[i];
    if (l.trim().startsWith('```')) {
      const cod: string[] = [];
      i++;
      while (i < linhas.length && !linhas[i].trim().startsWith('```')) cod.push(linhas[i++]);
      i++;
      blocos.push(<pre key={i} className="rounded bg-slate-950/70 p-2 text-xs text-slate-300 overflow-x-auto">{cod.join('\n')}</pre>);
      continue;
    }
    const h = /^(#{1,4})\s+(.*)$/.exec(l);
    if (h) { blocos.push(<div key={i} className="font-semibold text-slate-100 mt-1">{inline(h[2], `h${i}`)}</div>); i++; continue; }
    const lista = (re: RegExp) => {
      const itens: React.ReactNode[] = [];
      while (i < linhas.length && re.test(linhas[i])) {
        const sub = /^\s{2,}/.test(linhas[i]);
        itens.push(<li key={i} className={sub ? 'ml-4' : ''}>{inline(linhas[i].replace(re, ''), `li${i}`)}</li>);
        i++;
      }
      return itens;
    };
    if (/^\s*[-*]\s+/.test(l)) { blocos.push(<ul key={i} className="list-disc ml-5 space-y-0.5">{lista(/^\s*[-*]\s+/)}</ul>); continue; }
    if (/^\s*\d+[.)]\s+/.test(l)) { blocos.push(<ol key={i} className="list-decimal ml-5 space-y-0.5">{lista(/^\s*\d+[.)]\s+/)}</ol>); continue; }
    if (l.trim() === '') { i++; continue; }
    const par: string[] = [];
    while (i < linhas.length && linhas[i].trim() !== '' && !/^(\s*[-*]\s+|\s*\d+[.)]\s+|#{1,4}\s|\s*```)/.test(linhas[i])) par.push(linhas[i++]);
    blocos.push(<p key={i} className="whitespace-pre-wrap">{inline(par.join('\n'), `p${i}`)}</p>);
  }
  return <div className="break-words text-slate-200 space-y-1.5">{blocos}</div>;
}

/** 👍/👎 + comentário numa resposta do agente. Vale a última avaliação de cada conta; o comentário vai para o ajuste do
 * agente (observador). */
function AvaliarResposta({ incidenteId, m }: { incidenteId: number; m: Mensagem }) {
  const qc = useQueryClient();
  const minha = m.avaliacoes.find((a) => a.minha);
  const [aberto, setAberto] = useState<null | boolean>(null);
  const [comentario, setComentario] = useState('');
  const salvar = useMutation({
    mutationFn: (v: { util: boolean; comentario: string | null }) => api.post(`/api/noc-agente/mensagens/${m.id}/avaliacao`, v),
    onSuccess: () => { setAberto(null); setComentario(''); qc.invalidateQueries({ queryKey: ['noc-agente', 'incidente', incidenteId] }); }
  });
  const uteis = m.avaliacoes.filter((a) => a.util).length, ruins = m.avaliacoes.length - uteis;
  const btn = (on: boolean) => `px-1.5 py-0.5 rounded ring-1 ${on ? 'ring-emerald-500 bg-emerald-950/50' : 'ring-slate-700 hover:bg-slate-700/60'}`;
  const clicar = (util: boolean) => {
    // 👍 grava direto; 👎 pede o motivo (é o que vira ajuste), mas deixa enviar sem
    if (util) salvar.mutate({ util: true, comentario: null });
    else setAberto(false);
  };
  return (
    <div className="mt-1.5 space-y-1">
      <div className="flex items-center gap-1.5 text-xs text-slate-400">
        <button className={btn(minha?.util === true)} title="resposta útil" disabled={salvar.isPending} onClick={() => clicar(true)}>👍{uteis ? ` ${uteis}` : ''}</button>
        <button className={btn(minha?.util === false)} title="resposta ruim" disabled={salvar.isPending} onClick={() => clicar(false)}>👎{ruins ? ` ${ruins}` : ''}</button>
        <button className="hover:text-slate-200" onClick={() => setAberto(aberto === null ? (minha?.util ?? true) : null)}>comentar</button>
        {m.avaliacoes.filter((a) => a.comentario).map((a, i) => (
          <span key={i} className="text-slate-500 truncate max-w-xs" title={`${a.usuario}: ${a.comentario}`}>
            · {a.util ? '👍' : '👎'} {a.usuario}: {a.comentario}
          </span>
        ))}
        {salvar.isError && <span className="text-rose-400">falha ao salvar</span>}
      </div>
      {aberto !== null && (
        <div className="flex gap-2 items-end">
          <textarea className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs" rows={2} autoFocus
            value={comentario} onChange={(e) => setComentario(e.target.value)}
            placeholder={aberto ? 'o que ajudou (opcional)' : 'o que faltou ou estava errado (vira ajuste do agente)'} />
          <button className="px-2 py-1 rounded bg-emerald-700 hover:bg-emerald-600 text-xs disabled:opacity-40" disabled={salvar.isPending}
            onClick={() => salvar.mutate({ util: aberto, comentario: comentario.trim() || null })}>
            {aberto ? '👍 enviar' : '👎 enviar'}
          </button>
          <button className="text-xs text-slate-500 hover:text-slate-300" onClick={() => setAberto(null)}>cancelar</button>
        </div>
      )}
    </div>
  );
}

function Consultas({ tools }: { tools: Tool[] }) {
  return (
    <Card titulo={`Consultas do agente (${tools.length})`}>
      <details className="text-xs">
        <summary className="cursor-pointer text-slate-400">ver comandos e consultas</summary>
        <div className="space-y-1 mt-1 max-h-80 overflow-y-auto">
          {tools.map((t, i) => (
            <div key={i} className={t.permitido ? 'text-slate-300' : 'text-rose-400'}>
              <span className="text-slate-500">{t.tool.replace(/^mcp__/, '').replace('__', ' ')}</span>{' '}
              <span className="font-mono break-all">{String((t.args as any).command ?? (t.args as any).query ?? (t.args as any).key ?? (t.args as any).search ?? '')}</span>
              {!t.permitido && <span> — recusado: {t.motivo}</span>}
            </div>
          ))}
        </div>
      </details>
    </Card>
  );
}

function Tarefas() {
  const [agente, setAgente] = useState<'' | 'noc-zabbix' | 'noc-redefora'>('');
  const [estado, setEstado] = useState<'abertas' | 'todas'>('abertas');
  const params = new URLSearchParams({ estado, take: '300' });
  if (agente) params.set('agente', agente);
  const q = useQuery<{ tarefas: Tarefa[] }>({
    queryKey: ['noc-agente', 'tarefas', params.toString()],
    queryFn: () => api.get(`/api/noc-agente/tarefas?${params}`),
    refetchInterval: 30_000,
    placeholderData: (prev) => prev
  });
  const f = (on: boolean) => `px-2 py-1 rounded text-xs ring-1 ${on ? 'ring-emerald-500 text-emerald-300' : 'ring-slate-700 text-slate-400'}`;
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xl font-semibold">Agente NOC IA <span className="text-sm text-slate-500">— tarefas dos agentes (inbox)</span></h2>
        <Abas atual="tarefas" />
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button className={f(agente === '')} onClick={() => setAgente('')}>Todos</button>
        {AGENTES.map((a) => <button key={a.id} className={f(agente === a.id)} onClick={() => setAgente(a.id)}>{a.nome}</button>)}
        <span className="mx-1 h-4 w-px bg-slate-700" />
        <button className={f(estado === 'abertas')} onClick={() => setEstado('abertas')}>Abertas</button>
        <button className={f(estado === 'todas')} onClick={() => setEstado('todas')}>Todas</button>
        <span className="ml-auto text-xs text-slate-500">{q.data ? `${q.data.tarefas.length} tarefa(s)` : ''}{q.isFetching ? ' · atualizando…' : ''}</span>
      </div>
      <div className="rounded border border-slate-800 bg-slate-900/60 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-800/60 text-slate-400">
            <tr>
              <th className="text-left px-3 py-2">#</th>
              <th className="text-left px-3 py-2">Agente</th>
              <th className="text-left px-3 py-2">Tarefa</th>
              <th className="text-left px-3 py-2">Prioridade</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Atualizada</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && <tr><td colSpan={6} className="p-4 text-center text-slate-500">carregando…</td></tr>}
            {q.isError && <tr><td colSpan={6} className="p-4 text-center text-rose-400">erro ao carregar</td></tr>}
            {q.data?.tarefas.map((t) => (
              <tr key={t.inboxId} className="border-t border-slate-800 hover:bg-slate-800/40">
                <td className="px-3 py-1.5 font-mono text-xs text-slate-400">{t.inboxId}</td>
                <td className="px-3 py-1.5 text-xs text-cyan-300 whitespace-nowrap">{t.agente}</td>
                <td className="px-3 py-1.5 text-slate-200"><div className="truncate max-w-xl" title={t.descricao}>{t.titulo}</div></td>
                <td className={`px-3 py-1.5 text-xs ${PRIO_COR[t.prioridade] ?? 'text-slate-300'}`}>{t.prioridade}</td>
                <td className="px-3 py-1.5 text-xs text-slate-300">{t.status}</td>
                <td className="px-3 py-1.5 font-mono text-xs text-slate-400 whitespace-nowrap">{dt(t.atualizadoEm)}</td>
              </tr>
            ))}
            {q.data?.tarefas.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-slate-500">nenhuma tarefa</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-slate-500">Fechar ou mudar uma tarefa é no inbox do Jorge; esta lista é só leitura e atualiza em até 1 min.</div>
    </div>
  );
}

function Chats() {
  const [escopo, setEscopo] = useState<'meus' | 'todos'>('meus');
  const [dias, setDias] = useState(30);
  const [texto, setTexto] = useState('');
  const [busca, setBusca] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setBusca(texto.trim()), 400);
    return () => clearTimeout(t);
  }, [texto]);
  const params = new URLSearchParams({ escopo, take: '500' });
  if (busca) params.set('q', busca);
  const q = useQuery<{ chats: ChatLinha[] }>({
    queryKey: ['noc-agente', 'chats', params.toString()],
    queryFn: () => api.get(`/api/noc-agente/chats?${params}`),
    refetchInterval: 20_000,
    placeholderData: (prev) => prev
  });
  const consumo = useQuery<{ usuarios: ConsumoUsuario[] }>({
    queryKey: ['noc-agente', 'chats-consumo', dias],
    queryFn: () => api.get(`/api/noc-agente/chats/consumo?dias=${dias}`),
    refetchInterval: 60_000
  });
  const f = (on: boolean) => `px-2 py-1 rounded text-xs ring-1 ${on ? 'ring-emerald-500 text-emerald-300' : 'ring-slate-700 text-slate-400'}`;
  const sel = 'bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200';
  const total = (consumo.data?.usuarios ?? []).reduce((a, u) => a + u.custoUsd, 0);
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xl font-semibold">Agente NOC IA <span className="text-sm text-slate-500">— chats (só leitura)</span></h2>
        <Abas atual="chats" />
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button className="px-3 py-1.5 rounded bg-emerald-700 hover:bg-emerald-600 text-sm" onClick={() => go('chats/novo')}>+ Novo chat</button>
        <span className="mx-1 h-4 w-px bg-slate-700" />
        <button className={f(escopo === 'meus')} onClick={() => setEscopo('meus')}>Meus</button>
        <button className={f(escopo === 'todos')} onClick={() => setEscopo('todos')}>Todos</button>
        <input className={`${sel} w-56`} value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="buscar #id, pergunta, usuário" />
        <span className="ml-auto text-xs text-slate-500">{q.data ? `${q.data.chats.length} chat(s)` : ''}{q.isFetching ? ' · atualizando…' : ''}</span>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 rounded border border-slate-800 bg-slate-900/60 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/60 text-slate-400">
              <tr>
                <th className="text-left px-3 py-2">#</th>
                <th className="text-left px-3 py-2">Agente</th>
                <th className="text-left px-3 py-2">Pergunta</th>
                <th className="text-left px-3 py-2">Quem</th>
                <th className="text-left px-3 py-2">Última</th>
                <th className="text-right px-3 py-2">Perguntas</th>
                <th className="text-right px-3 py-2">👍/👎</th>
                <th className="text-right px-3 py-2">Custo</th>
              </tr>
            </thead>
            <tbody>
              {q.isLoading && <tr><td colSpan={8} className="p-4 text-center text-slate-500">carregando…</td></tr>}
              {q.isError && <tr><td colSpan={8} className="p-4 text-center text-rose-400">erro ao carregar</td></tr>}
              {q.data?.chats.map((c) => (
                <tr key={c.id} className="border-t border-slate-800 hover:bg-slate-800/40 cursor-pointer" onClick={() => go(String(c.id))}>
                  <td className="px-3 py-1.5 font-mono text-xs text-slate-400">{c.id}</td>
                  <td className="px-3 py-1.5 text-xs text-cyan-300 whitespace-nowrap">{c.agente ?? <span className="text-slate-600">—</span>}</td>
                  <td className="px-3 py-1.5 text-slate-200">
                    <div className="truncate max-w-md" title={c.titulo}>{c.titulo}</div>
                    {c.ultimoPapel === 'usuario' && <div className="text-[11px] text-cyan-400">aguardando o agente…</div>}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-slate-400" title={c.criadoPorEmail ?? ''}>{c.criadoPor ?? '—'}</td>
                  <td className="px-3 py-1.5 font-mono text-xs text-slate-400 whitespace-nowrap">{dt(c.ultimaEm)}</td>
                  <td className="px-3 py-1.5 text-xs text-right text-slate-400">{c.perguntas}</td>
                  <td className="px-3 py-1.5 text-xs text-right whitespace-nowrap">
                    <span className="text-emerald-400">{c.uteis || ''}</span>{c.uteis && c.ruins ? ' / ' : ''}<span className="text-rose-400">{c.ruins || ''}</span>
                    {!c.uteis && !c.ruins && <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-right text-slate-400 whitespace-nowrap">{usd(c.custoUsd)}</td>
                </tr>
              ))}
              {q.data?.chats.length === 0 && <tr><td colSpan={8} className="p-4 text-center text-slate-500">nenhum chat</td></tr>}
            </tbody>
          </table>
        </div>
        <Card titulo="Consumo por usuário">
          <select className={sel} value={dias} onChange={(e) => setDias(Number(e.target.value))}>
            <option value={1}>últimas 24h</option>
            <option value={7}>últimos 7 dias</option>
            <option value={30}>últimos 30 dias</option>
            <option value={0}>tudo</option>
          </select>
          <table className="w-full text-xs">
            <thead className="text-slate-400">
              <tr><th className="text-left py-1">Conta</th><th className="text-right">Chats</th><th className="text-right">👍/👎</th><th className="text-right">Tokens</th><th className="text-right">Custo</th></tr>
            </thead>
            <tbody>
              {consumo.data?.usuarios.map((u) => (
                <tr key={u.email ?? u.usuario ?? '-'} className="border-t border-slate-800">
                  <td className="py-1 text-slate-300">
                    {u.usuario ?? '—'}
                    {u.email && <div className="text-[10px] text-slate-500">{u.email}</div>}
                  </td>
                  <td className="text-right text-slate-400">{u.chats}</td>
                  <td className="text-right whitespace-nowrap"><span className="text-emerald-400">{u.uteis}</span><span className="text-slate-600"> / </span><span className="text-rose-400">{u.ruins}</span></td>
                  <td className="text-right text-slate-400" title={`${u.respostas} resposta(s)`}>{tok(u.tokensEntrada)} / {tok(u.tokensSaida)}</td>
                  <td className="text-right text-slate-200">{usd(u.custoUsd)}</td>
                </tr>
              ))}
              {consumo.data?.usuarios.length === 0 && <tr><td colSpan={5} className="py-2 text-center text-slate-500">sem uso no período</td></tr>}
            </tbody>
            {!!consumo.data?.usuarios.length && (
              <tfoot><tr className="border-t border-slate-700"><td className="py-1 text-slate-400">Total</td><td /><td /><td /><td className="text-right text-slate-200">{usd(total)}</td></tr></tfoot>
            )}
          </table>
          <div className="text-[11px] text-slate-500">por conta do NMS (e-mail); tokens = entrada (com cache) / saída; custo atribuído a quem abriu o chat</div>
        </Card>
      </div>
    </div>
  );
}

type Alarme = {
  eventid: string; triggerid: string; nome: string; severidade: number; desde: string; reconhecido: boolean;
  host: string; grupos: string[];
};
const SEV = ['n/c', 'info', 'warning', 'média', 'alta', 'desastre'];
const SEV_COR = ['text-slate-500', 'text-sky-400', 'text-amber-300', 'text-amber-400', 'text-orange-400', 'text-rose-400'];

function Alarmes({ isAdmin }: { isAdmin: boolean }) {
  const [min, setMin] = useState(2);
  const [q, setQ] = useState('');
  const lista = useQuery({
    queryKey: ['noc-agente', 'alarmes', min],
    queryFn: () => api.get<{ alarmes: Alarme[] }>(`/api/noc-agente/alarmes?min=${min}`),
    refetchInterval: 15_000
  });
  const analisar = useMutation({
    mutationFn: (eventid: string) => api.post<{ id: number }>('/api/noc-agente/alarmes/analisar', { eventid }),
    onSuccess: (r) => go(String(r.id))
  });
  const termo = q.trim().toLowerCase();
  const linhas = (lista.data?.alarmes ?? []).filter((a) =>
    !termo || `${a.host} ${a.nome} ${a.grupos.join(' ')}`.toLowerCase().includes(termo));
  return (
    <div className="p-6 space-y-4">
      <Abas atual="alarmes" />
      <div className="flex items-center gap-3 text-sm">
        <select className="bg-slate-800 border border-slate-700 rounded px-2 py-1" value={min} onChange={(e) => setMin(Number(e.target.value))}>
          {[0, 1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>severidade ≥ {SEV[n]}</option>)}
        </select>
        <input className="bg-slate-800 border border-slate-700 rounded px-2 py-1 w-64" placeholder="buscar host, alarme ou grupo"
          value={q} onChange={(e) => setQ(e.target.value)} />
        <span className="text-slate-500">{linhas.length} alarme(s) · atualiza a cada 15 s</span>
        {!isAdmin && <span className="text-slate-500">botão "Analisar com IA": fale com o Jorge</span>}
      </div>
      {lista.isError && <div className="text-xs text-rose-400">Zabbix indisponível</div>}
      {analisar.isError && <div className="text-xs text-rose-400">falha ao abrir a análise (o alarme pode ter sido resolvido)</div>}
      <table className="w-full text-sm">
        <thead className="text-left text-slate-400">
          <tr><th className="py-1">Sev.</th><th>Host</th><th>Alarme</th><th>Desde</th><th>Ack</th>{isAdmin && <th />}</tr>
        </thead>
        <tbody>
          {linhas.map((a) => (
            <tr key={a.eventid} className="border-t border-slate-800">
              <td className={`py-1 ${SEV_COR[a.severidade] ?? ''}`}>{SEV[a.severidade] ?? a.severidade}</td>
              <td>{a.host}</td>
              <td>{a.nome}</td>
              <td className="text-slate-400">{dt(a.desde)}</td>
              <td>{a.reconhecido ? '✓' : ''}</td>
              {isAdmin && (
                <td>
                  <button className="px-2 py-0.5 rounded bg-emerald-700 hover:bg-emerald-600 text-xs disabled:opacity-40"
                    disabled={analisar.isPending} onClick={() => analisar.mutate(a.eventid)}>Analisar com IA</button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NovoChat() {
  const qc = useQueryClient();
  const [texto, setTexto] = useState('');
  const [agente, setAgente] = useState<'noc-zabbix' | 'noc-redefora'>('noc-zabbix');
  const criar = useMutation({
    mutationFn: () => api.post<{ id: number }>('/api/noc-agente/chats', { texto, agente }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['noc-agente', 'chats'] }); go(String(r.id)); }
  });
  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <div>
        <button className="text-xs text-slate-400 hover:text-slate-200" onClick={() => go('chats')}>← chats</button>
        <h2 className="text-xl font-semibold">Novo chat com o Agente NOC IA</h2>
        <div className="text-sm text-slate-400">
          Escolha com quem falar. O agente consulta só em leitura e responde em até ~1 min; nunca altera nada. A conversa, as
          consultas e o custo ficam registrados, e um problema real vira tarefa na aba Tarefas.
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {AGENTES.map((a) => (
          <button key={a.id} type="button" onClick={() => setAgente(a.id)}
            className={`text-left rounded border px-3 py-2 ${agente === a.id ? 'border-emerald-500 bg-emerald-900/20' : 'border-slate-700 hover:bg-slate-800/60'}`}>
            <div className="text-sm font-semibold text-slate-100">{a.nome}</div>
            <div className="text-xs text-slate-400">{a.desc}</div>
          </button>
        ))}
      </div>
      <textarea className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm" rows={5} autoFocus
        value={texto} onChange={(e) => setTexto(e.target.value)}
        placeholder="ex.: cliente login fulano com ping alto / como está o BGP do core agora? / a OLT de Canapi teve queda hoje? / qual o Rx da uplink do switch X?" />
      <div className="text-xs text-slate-500">
        Dica: informe o login PPPoE (ele acha BRAS, ONU, sinal e quedas pelo IXC), o nome do equipamento ou o POP, e o horário do problema.
      </div>
      <button className="px-3 py-1.5 rounded bg-emerald-700 hover:bg-emerald-600 text-sm disabled:opacity-40"
        disabled={!texto.trim() || criar.isPending} onClick={() => criar.mutate()}>Iniciar chat</button>
      {criar.isError && <div className="text-xs text-rose-400">falha ao criar o chat</div>}
    </div>
  );
}

function Avaliar({ id }: { id: number }) {
  const qc = useQueryClient();
  const [diagnostico, setDiagnostico] = useState<'' | 'certo' | 'parcial' | 'errado'>('');
  const [osProcedente, setOsProcedente] = useState<'' | 'sim' | 'nao'>('');
  const [ruido, setRuido] = useState(false);
  const [comentario, setComentario] = useState('');
  const salvar = useMutation({
    mutationFn: () => api.post(`/api/noc-agente/incidentes/${id}/avaliacao`, {
      diagnostico: diagnostico || null,
      osProcedente: osProcedente === '' ? null : osProcedente === 'sim',
      ruido,
      comentario: comentario || null
    }),
    onSuccess: () => {
      setDiagnostico(''); setOsProcedente(''); setRuido(false); setComentario('');
      qc.invalidateQueries({ queryKey: ['noc-agente'] });
    }
  });
  const opt = (on: boolean) => `px-2 py-1 rounded text-xs ring-1 ${on ? 'ring-emerald-500 text-emerald-300' : 'ring-slate-700 text-slate-400'}`;
  return (
    <Card titulo="Avaliar">
      <div className="space-y-2 text-sm">
        <div className="flex gap-1 flex-wrap">
          {(['certo', 'parcial', 'errado'] as const).map((v) => (
            <button key={v} className={opt(diagnostico === v)} onClick={() => setDiagnostico(diagnostico === v ? '' : v)}>{v}</button>
          ))}
        </div>
        <div className="flex gap-1 items-center text-xs text-slate-400">
          OS procedente?
          <button className={opt(osProcedente === 'sim')} onClick={() => setOsProcedente(osProcedente === 'sim' ? '' : 'sim')}>sim</button>
          <button className={opt(osProcedente === 'nao')} onClick={() => setOsProcedente(osProcedente === 'nao' ? '' : 'nao')}>não</button>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-400">
          <input type="checkbox" checked={ruido} onChange={(e) => setRuido(e.target.checked)} /> alarme era ruído
        </label>
        <textarea className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm" rows={3}
          value={comentario} onChange={(e) => setComentario(e.target.value)}
          placeholder="o que o agente acertou/errou (vira ajuste no conhecimento dele)" />
        <button className="px-3 py-1.5 rounded bg-emerald-700 hover:bg-emerald-600 text-sm disabled:opacity-40"
          disabled={salvar.isPending || (!diagnostico && osProcedente === '' && !ruido && !comentario.trim())}
          onClick={() => salvar.mutate()}>Salvar avaliação</button>
        {salvar.isSuccess && <div className="text-xs text-emerald-400">avaliação registrada</div>}
        {salvar.isError && <div className="text-xs text-rose-400">falha ao salvar</div>}
      </div>
    </Card>
  );
}

function Maturidade() {
  const q = useQuery<{ kpi: Record<string, number | null>; uso: Array<{ tool: string; aceitas: number; recusadas: number }> }>({
    queryKey: ['noc-agente', 'maturidade'],
    queryFn: () => api.get('/api/noc-agente/maturidade'),
    refetchInterval: 60_000
  });
  const k = q.data?.kpi ?? {};
  const n = (v: number | null | undefined) => v ?? 0;
  const pct = (a: number, b: number) => (b ? Math.round((100 * a) / b) : 0);
  const acerto = pct(n(k.certoParcial), n(k.avaliados));
  const precisaoOs = pct(n(k.osVp), n(k.osVp) + n(k.osFp));
  const homologa = n(k.avaliados) >= 50 && acerto >= 90;
  const osReal = n(k.osVp) + n(k.osFp) >= 20 && precisaoOs >= 90;
  const Kpi = ({ l, v }: { l: string; v: string | number }) => (
    <div className="rounded border border-slate-800 bg-slate-900/60 p-3">
      <div className="text-xs text-slate-400">{l}</div>
      <div className="text-lg font-semibold">{v}</div>
    </div>
  );
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xl font-semibold">Agente NOC IA <span className="text-sm text-slate-500">— maturidade</span></h2>
        <Abas atual="maturidade" />
      </div>
      {q.isLoading && <div className="text-slate-400">carregando…</div>}
      {q.data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi l="Incidentes" v={n(k.incidentes)} />
            <Kpi l="Diagnosticados" v={n(k.diagnosticados)} />
            <Kpi l="Resolvidos antes do diagnóstico" v={n(k.autoResolvidos)} />
            <Kpi l="Avaliados" v={n(k.avaliados)} />
            <Kpi l="Certo ou parcial" v={`${acerto}%`} />
            <Kpi l="Ruído apontado" v={n(k.ruido)} />
            <Kpi l="OS sombra (abriria)" v={n(k.osSombra)} />
            <Kpi l="Precisão da OS sombra" v={`${precisaoOs}% (${n(k.osVp)} VP / ${n(k.osFp)} FP / ${n(k.osFn)} FN)`} />
            <Kpi l="Pico de incidentes/hora" v={n(k.picoIncHora)} />
            <Kpi l="Pico de OS/hora" v={n(k.picoOsHora)} />
            <Kpi l="Custo médio / total" v={`${usd(k.custoMedio)} / ${usd(k.custoTotal)}`} />
            <Kpi l="Minutos até o diagnóstico (mediana, sem replay)" v={k.minAteDiag != null ? Math.round(k.minAteDiag) : '—'} />
            <Kpi l="Chats livres (custo total)" v={`${n(k.chats)} (${usd(k.custoChats)})`} />
            <Kpi l="Respostas de chat 👍 / 👎" v={`${n(k.respostasUteis)} / ${n(k.respostasRuins)}`} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card titulo="Gate de homologação">
              <div className={homologa ? 'text-emerald-400' : 'text-slate-400'}>{homologa ? 'atingido' : 'ainda não'}</div>
              <div className="text-xs text-slate-500">≥ 50 incidentes avaliados e ≥ 90% certo ou parcial ({n(k.avaliados)} avaliados, {acerto}%)</div>
            </Card>
            <Card titulo="Gate de OS real no IXC">
              <div className={osReal ? 'text-emerald-400' : 'text-slate-400'}>{osReal ? 'atingido' : 'ainda não'}</div>
              <div className="text-xs text-slate-500">precisão da OS sombra ≥ 90% com ≥ 20 OS avaliadas e sem pico de flood</div>
            </Card>
          </div>
          <Card titulo="Uso de tools pelo agente">
            <table className="w-full text-xs">
              <thead className="text-slate-400"><tr><th className="text-left py-1">Tool</th><th className="text-right">Aceitas</th><th className="text-right">Recusadas</th></tr></thead>
              <tbody>
                {q.data.uso.map((u) => (
                  <tr key={u.tool} className="border-t border-slate-800">
                    <td className="py-1 font-mono">{u.tool.replace(/^mcp__/, '')}</td>
                    <td className="text-right">{u.aceitas}</td>
                    <td className={`text-right ${u.recusadas ? 'text-rose-400' : 'text-slate-500'}`}>{u.recusadas}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}
